import { createContext, useContext } from 'react'
import { Navigate, Outlet } from 'react-router'
import { LogOut, ShieldOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FullPageLoader } from '@/components/full-page-loader'
import { ErrorState } from '@/components/data/states'
import { useAuth } from '@/hooks/use-auth'
import { isForbidden } from '@/lib/api'
import { logout } from '@/lib/firebase'
import { useMe } from '@/lib/queries'
import type { Me } from '@/lib/types'

const AccessContext = createContext<{ me: Me } | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useAccess() {
  const ctx = useContext(AccessContext)
  if (!ctx) throw new Error('useAccess must be used inside RequireAccess')
  return ctx
}

/** Signed in, and known to the API as a platform admin; otherwise sends you to sign in or says why not. */
export function RequireAccess() {
  const { user, loading } = useAuth()
  const me = useMe(!!user)

  if (loading) return <FullPageLoader />
  if (!user) return <Navigate to="/login" replace />
  if (me.isPending) return <FullPageLoader />

  if (isForbidden(me.error)) {
    return (
      <div className="grid min-h-svh place-items-center p-6">
        <div className="panel max-w-md p-8 text-center">
          <ShieldOff className="mx-auto mb-3 size-7 text-muted-foreground" aria-hidden />
          <h1 className="text-lg font-semibold">This account can't open the admin panel</h1>
          <p className="mt-2 text-muted-foreground">
            {user.email} is not on the list of platform admins. Ask someone who is to add the address, or sign in with another account.
          </p>
          <Button variant="outline" className="mt-5" onClick={() => logout()}>
            <LogOut /> Sign out
          </Button>
        </div>
      </div>
    )
  }
  if (me.isError) {
    return (
      <div className="grid min-h-svh place-items-center">
        <ErrorState error={me.error} onRetry={() => me.refetch()} />
      </div>
    )
  }
  return (
    <AccessContext.Provider value={{ me: me.data }}>
      <Outlet />
    </AccessContext.Provider>
  )
}
